import {Context} from 'chartjs-plugin-datalabels';

import {ChartTooltip} from '../../../../../../component/chart-tooltip/chart-tooltip';
import {ChartTooltipDivider} from '../../../../../../component/chart-tooltip/chart-tooltip-divider';
import {getCompareColor} from '../../../../../../component/chart-tooltip/chart-tooltip-helper';
import {ChartTooltipRow} from '../../../../../../component/chart-tooltip/chart-tooltip-row';
import {ChartTooltipTitle} from '../../../../../../component/chart-tooltip/chart-tooltip-title';
import {DefaultTooltipStaticPropsType} from '../../../../../../component/chart-tooltip/default-chart-tooltip';
import {defaultLineChartOptions} from '../../../../../../layout/chart/line-chart/line-chart-helper';
import {ChartJsLineOptionsType} from '../../../../../../layout/chart/line-chart/line-chart-type';
import {radarChartOptions} from '../../../../../../layout/chart/radar-chart/radar-chart-helper';
import {ChartJsRadarOptionsType, RadarDataSetType} from '../../../../../../layout/chart/radar-chart/radar-chart-type';
import {customReactChartTooltip} from '../../../../../../layout/chart/utils/chart-helper';
import {ReviewAspectRecordType} from '../../../../../../service/reivew-analysis/review-aspects/review-aspects-type';
import {
    SentimentDataType,
    SentimentEnum,
} from '../../../../../../service/reivew-analysis/review-sentiment/review-sentiment-type';

type TonalityPeriodDataType = ReviewAspectRecordType['tonalityCurrentPeriodData'];

export function getTonalitySentimentData(tonalityData?: TonalityPeriodDataType | null): SentimentDataType {
    return {
        [SentimentEnum.Negative]: tonalityData?.negative ?? 0,
        [SentimentEnum.Neutral]: tonalityData?.neutral ?? 0,
        [SentimentEnum.Positive]: tonalityData?.positive ?? 0,
        [SentimentEnum.Unspecified]: 0,
    };
}

const lineColors = [
    '#E57373',
    '#64B5F6',
    '#81C784',
    '#FFD54F',
    '#BA68C8',
    '#4DD0E1',
    '#AED581',
    '#F06292',
    '#FF8A65',
    '#A1887F',
] as const;

export function getAspectsToneColor(index: number): string {
    const colorIndex = index % lineColors.length;

    const color = lineColors[colorIndex];

    return color ?? lineColors[0];
}

export function getAspectsToneChartOptions(tooltipOptions: DefaultTooltipStaticPropsType): ChartJsLineOptionsType {
    return {
        ...defaultLineChartOptions,
        scales: {
            y: {
                min: -100,
                max: 100,
            },
        },
        plugins: {
            ...defaultLineChartOptions.plugins,
            tooltip: {
                enabled: false,
                yAlign: 'top',
                xAlign: 'center',
                position: 'nearest',
                external: (context: Context) => {
                    customReactChartTooltip({
                        context,
                        componentRenderer: (index, tooltipRendererOptions) => {
                            const label = context.tooltip.dataPoints[0]?.label || '';

                            const datasetLabelsToShowInTooltip = new Set(
                                new Set(
                                    new Set(
                                        context.tooltip.dataPoints?.map((item) => {
                                            return item.dataset.label;
                                        })
                                    )
                                ).values()
                            );

                            const mainPeriodDatasets = tooltipOptions.data.datasets.filter((dataset) => {
                                return datasetLabelsToShowInTooltip.has(dataset.label);
                            });

                            const comparePeriodDatasets =
                                tooltipOptions.compareData?.datasets.filter((dataset) => {
                                    return datasetLabelsToShowInTooltip.has(dataset.label);
                                }) || [];

                            return (
                                <ChartTooltip position={tooltipRendererOptions.position}>
                                    <ChartTooltipTitle title={label} />
                                    {mainPeriodDatasets.map((dataset) => {
                                        return (
                                            <ChartTooltipRow
                                                color={String(dataset.color)}
                                                key={dataset.label}
                                                label={dataset.label}
                                                value={dataset.values?.[index] || 0}
                                            />
                                        );
                                    })}

                                    {comparePeriodDatasets.length > 0 && (
                                        <>
                                            <ChartTooltipDivider />
                                            {comparePeriodDatasets.map((dataset) => {
                                                return (
                                                    <ChartTooltipRow
                                                        color={getCompareColor(String(dataset.color))}
                                                        key={dataset.label}
                                                        label={dataset.label}
                                                        value={dataset.values?.[index] || 0}
                                                    />
                                                );
                                            })}
                                        </>
                                    )}
                                </ChartTooltip>
                            );
                        },
                    });
                },
            },
            legend: {
                display: false,
            },
        },
    };
}

export function getReviewCountByAspectsChartOptions(options: {
    aspects: Array<string>;
    mainPeriodDataset: RadarDataSetType;
    tooltipLabelFormatter: (label: string, compareLabel?: boolean) => string;
    comparePeriodDataset: RadarDataSetType;
    compareMode: boolean;
}): ChartJsRadarOptionsType {
    const {aspects, mainPeriodDataset, tooltipLabelFormatter, comparePeriodDataset, compareMode} = options;

    return {
        ...radarChartOptions,
        scales: {
            // eslint-disable-next-line id-length
            r: {
                min: -100,
                max: 100,
            },
        },
        plugins: {
            ...defaultLineChartOptions.plugins,

            tooltip: {
                enabled: false,
                yAlign: 'top',
                xAlign: 'center',
                position: 'nearest',
                external: (context: Context) =>
                    customReactChartTooltip({
                        context,
                        componentRenderer: (index, tooltipRendererOptions) => {
                            return (
                                <ChartTooltip position={tooltipRendererOptions?.position}>
                                    <ChartTooltipTitle title={tooltipLabelFormatter(mainPeriodDataset.label)} />

                                    <ChartTooltipRow
                                        color={mainPeriodDataset.color.lineColor}
                                        label={aspects[index] || ''}
                                        value={mainPeriodDataset.values[index] || 0}
                                    />

                                    {compareMode && (
                                        <>
                                            <ChartTooltipDivider />

                                            <ChartTooltipTitle
                                                title={tooltipLabelFormatter(comparePeriodDataset.label, true)}
                                            />

                                            <ChartTooltipRow
                                                color={comparePeriodDataset.color.lineColor}
                                                label={aspects[index] || ''}
                                                value={comparePeriodDataset.values[index] || 0}
                                            />
                                        </>
                                    )}
                                </ChartTooltip>
                            );
                        },
                    }),
            },
            legend: {
                display: false,
            },
        },
    };
}
